import { Link } from 'react-router';
import type { DayClose } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import type { Column } from '#client/components/ui/DataTable';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { Select } from '#client/components/ui/Select';
import { TextField } from '#client/components/ui/TextField';
import { formatMoney } from '#client/lib/format';
import { routes, worklistPath } from '#client/lib/routes';
import { DayCloseSessions } from './DayCloseSessions';
import { DrawerFigures, SummaryItem } from './DrawerFigures';
import { dayCloseSubtitle, describeWhose } from './day-close.logic';
import { useDayClose } from './useDayClose';

type Unpaid = DayClose['unpaid'][number];

const unpaidColumns: Column<Unpaid>[] = [
  {
    key: 'name',
    header: 'Patient',
    frozen: true,
    render: (row) => <Link to={routes.patient(row.patient_id)}>{row.name}</Link>,
  },
  {
    key: 'pin_no',
    header: 'PIN',
    width: '180px',
    render: (row) => <span className="ident">{row.pin_no}</span>,
  },
  {
    key: 'balance',
    header: 'Balance',
    width: '150px',
    align: 'end',
    render: (row) => <span className="tnum c-money--due">{formatMoney(row.balance)}</span>,
  },
];

/**
 * The end-of-shift summary.
 *
 * This is what replaces the spreadsheet's "Main Desk" and "Desk Stop" button
 * grids. Those were Excel's substitute for navigation, and this app has three
 * better surfaces already — the role-filtered sidebar, a dashboard that is a
 * real work queue, and Ctrl+K. What the grids could *not* do is answer the
 * question somebody actually has at 6 p.m.: does the drawer match, and what is
 * still open.
 *
 * Printed with the browser's own dialogue rather than a server-rendered PDF: it
 * is an internal note for the drawer, not a document handed to anyone, and a
 * whole print template for it would be a schema to maintain for one sheet a day.
 *
 * An admin sees the whole lab and can narrow it to one staff member; everyone
 * else sees only their own figures (see `useDayClose`).
 */
export function DayClosePage() {
  const { close, on, today, staff, canReviewAll, people, setDay, setStaff } = useDayClose();

  if (close.loading) return <PageLoading title="Day close" fullWidth />;
  if (close.error || !close.data) {
    return (
      <PageMessage title="Day close" fullWidth>
        {close.error ?? 'Could not load the day summary.'}
      </PageMessage>
    );
  }

  const day = close.data;
  const whose = describeWhose({ userId: day.user_id, canReviewAll, people });

  return (
    <Page
      title="Day close"
      subtitle={dayCloseSubtitle(whose, day.registrations, on)}
      fullWidth
      primaryAction={
        <Button variant="primary" onClick={() => window.print()}>
          Print summary
        </Button>
      }
      secondaryActions={
        <>
          {canReviewAll && (
            <Select
              label="Staff"
              hideLabel
              value={staff}
              placeholder="All staff"
              options={people.map((person) => ({ value: String(person.id), label: person.name }))}
              onChange={setStaff}
            />
          )}
          <TextField
            label="Day"
            hideLabel
            type="date"
            value={on}
            max={today}
            onChange={(event) => setDay(event.target.value)}
          />
        </>
      }
    >
      <div className="c-form-stack">
        <Card title="The drawer">
          <DrawerFigures figures={day} />
        </Card>
        <BenchCard day={day} />
        <BalancesCard unpaid={day.unpaid} />
        <DayCloseSessions sessions={day.sessions} onReviewed={close.refresh} />
      </div>
    </Page>
  );
}

function BenchCard({ day }: { day: Pick<DayClose, 'completed' | 'not_started'> }) {
  return (
    <Card title="The bench">
      <dl className="c-summary">
        <SummaryItem label="Tests completed today" value={String(day.completed)} />
        <SummaryItem
          label="Registered today, nothing entered"
          value={String(day.not_started)}
          tone={day.not_started > 0 ? 'watch' : undefined}
        />
      </dl>
      {day.not_started > 0 && (
        <p>
          <Link to={worklistPath({ status: 'registered' })}>
            Open the {day.not_started} that have not been started
          </Link>
        </p>
      )}
    </Card>
  );
}

function BalancesCard({ unpaid }: { unpaid: Unpaid[] }) {
  return (
    <Card title="Balances to chase" flush>
      {unpaid.length === 0 ? (
        <EmptyState
          title="Everything registered today is settled"
          body="No visit from this day has a balance outstanding."
        />
      ) : (
        <DataTable
          columns={unpaidColumns}
          rows={unpaid}
          rowKey={(row) => row.patient_id}
          caption="Visits registered today with a balance"
        />
      )}
    </Card>
  );
}
